import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, ScrollView, TouchableOpacity, StyleSheet } from 'react-native';
import { ChevronRight, MapPin } from 'lucide-react-native';
import { useTheme, FONT_SIZES, SPACING, TYPOGRAPHY, withWeight } from '../../theme';
import { Button, ErrorBanner, Input } from '../common';
import {
  forwardGeocode,
  reverseGeocode,
  placeAutocomplete,
  placeDetails,
  newSessionToken,
  type ForwardGeocodeResult,
  type PlaceSuggestion,
} from '../../utils/geocode';
import { LocationPickerMap } from '../structures/LocationPickerMap';

export interface LocationPickerBodyProps {
  initial: { latitude: number; longitude: number; address?: string } | null;
  onConfirm: (loc: { latitude: number; longitude: number; address?: string }) => void;
}

type Step = 'search' | 'confirm';

// Fallback when the user has no GPS and skips straight to the map — Big Bear
// Lake village, the primary REDI service area.
const FALLBACK_CENTROID = { latitude: 34.2439, longitude: -116.9114 };

/**
 * The "set observation location" body: search-by-address, or drop/drag a
 * pin, then confirm. A PanelStack push inside PostComposer
 * (panel-system.md Phase 3, "the checklist and before-post pickers become
 * drill-ins on every platform") — PostLocationSection pushes this instead of
 * opening it as an AdaptiveSheet dialog. Every prior mount was fresh (an
 * AdaptiveSheet kept this component alive across opens; a PanelStack push
 * instead mounts a new instance each time — see AddPropertyForm's doc
 * comment for the same reasoning), so the reset effect below now runs once
 * on mount instead of being keyed off a `visible` prop.
 */
export function LocationPickerBody({ initial, onConfirm }: LocationPickerBodyProps) {
  const { colors } = useTheme();

  const [step, setStep] = useState<Step>('search');
  const [query, setQuery] = useState(initial?.address ?? '');
  const [centroid, setCentroid] = useState<{ latitude: number; longitude: number } | null>(
    initial ? { latitude: initial.latitude, longitude: initial.longitude } : null
  );
  const [address, setAddress] = useState<string | undefined>(initial?.address);
  const [results, setResults] = useState<ForwardGeocodeResult[]>([]);
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // One Places session token per search session (reset on mount and after a
  // pick) so autocomplete + details bill as a single session.
  const sessionTokenRef = useRef<string>(newSessionToken());
  const autocompleteDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Set true right after a suggestion tap so writing its text back into the
  // input doesn't immediately re-query autocomplete.
  const suppressAutocompleteRef = useRef(false);

  const applyResult = useCallback((r: ForwardGeocodeResult) => {
    setCentroid(r.centroid);
    setAddress(r.formattedAddress);
    setResults([]);
    setSuggestions([]);
    setError(null);
    setStep('confirm');
  }, []);

  // As-you-type address suggestions (the primary path). Debounced so we don't
  // fire a Places call on every keystroke.
  const handleQueryChange = useCallback((text: string) => {
    setQuery(text);
    setError(null);
    if (suppressAutocompleteRef.current) {
      suppressAutocompleteRef.current = false;
      return;
    }
    if (autocompleteDebounceRef.current) clearTimeout(autocompleteDebounceRef.current);
    const q = text.trim();
    if (q.length < 3) {
      setSuggestions([]);
      return;
    }
    autocompleteDebounceRef.current = setTimeout(async () => {
      const found = await placeAutocomplete(q, sessionTokenRef.current);
      setSuggestions(found);
    }, 250);
  }, []);

  const handleSelectSuggestion = useCallback(async (s: PlaceSuggestion) => {
    setError(null);
    setSuggestions([]);
    setIsGeocoding(true);
    suppressAutocompleteRef.current = true;
    setQuery(s.secondaryText ? `${s.primaryText}, ${s.secondaryText}` : s.primaryText);
    try {
      const details = await placeDetails(s.placeId, sessionTokenRef.current);
      if (!details) {
        setError("Couldn't load that address. Try another, or drop a pin on the map.");
        return;
      }
      setCentroid(details.centroid);
      setAddress(
        details.formattedAddress ||
          (s.secondaryText ? `${s.primaryText}, ${s.secondaryText}` : s.primaryText)
      );
      setStep('confirm');
    } finally {
      setIsGeocoding(false);
      // Fresh session token for the next search.
      sessionTokenRef.current = newSessionToken();
    }
  }, []);

  const handleSearch = useCallback(async () => {
    const q = query.trim();
    if (!q) {
      setError('Enter an address to continue.');
      return;
    }
    setError(null);
    setIsGeocoding(true);
    try {
      const found = await forwardGeocode(q);
      if (found.length === 0) {
        setError("Couldn't find that address. Double-check the spelling.");
        return;
      }
      if (found.length === 1) {
        applyResult(found[0]);
      } else {
        setResults(found);
      }
    } catch {
      setError("Couldn't look up that address. Try again.");
    } finally {
      setIsGeocoding(false);
    }
  }, [query, applyResult]);

  const handleSkipToMap = useCallback(() => {
    setCentroid((c) => c ?? FALLBACK_CENTROID);
    setStep('confirm');
  }, []);

  // Refresh the displayed address when the user drags the pin. The previous
  // formattedAddress goes stale the moment the marker moves.
  const reverseDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleMapChange = useCallback((c: { latitude: number; longitude: number }) => {
    setCentroid(c);
    if (reverseDebounceRef.current) clearTimeout(reverseDebounceRef.current);
    reverseDebounceRef.current = setTimeout(async () => {
      const a = await reverseGeocode(c.latitude, c.longitude);
      if (a) setAddress(a);
    }, 400);
  }, []);

  useEffect(
    () => () => {
      if (reverseDebounceRef.current) clearTimeout(reverseDebounceRef.current);
      if (autocompleteDebounceRef.current) clearTimeout(autocompleteDebounceRef.current);
    },
    []
  );

  // onConfirm flips the parent's `visible`, which drives the dismissal.
  const handleConfirm = useCallback(() => {
    if (!centroid) return;
    onConfirm({ latitude: centroid.latitude, longitude: centroid.longitude, address });
  }, [centroid, address, onConfirm]);

  const candidateRows = useMemo(() => results.slice(0, 4), [results]);

  return (
    <ScrollView
      testID="location-picker-sheet"
      contentContainerStyle={styles.content}
      keyboardShouldPersistTaps="handled">
      <Text style={[styles.blurb, { color: colors.textSecondary }]}>
        {step === 'search'
          ? 'Start typing your address and pick it from the list.'
          : 'Drag the pin to fine-tune the exact spot.'}
      </Text>

      {step === 'search' && (
        <>
          <Input
            label="Address"
            value={query}
            onChangeText={handleQueryChange}
            onClear={() => {
              setQuery('');
              setSuggestions([]);
              setResults([]);
              setError(null);
            }}
            placeholder="123 Main St, Big Bear Lake, CA"
            autoCapitalize="words"
            autoCorrect={false}
            returnKeyType="search"
            onSubmitEditing={handleSearch}
          />

          {/* Primary path: tap an as-you-type suggestion. Hairline rows
              (mockup .rowlink), not bordered boxes. */}
          {suggestions.length > 0 && (
            <View style={[styles.candidates, { borderTopColor: colors.border }]}>
              {suggestions.map((s) => (
                <TouchableOpacity
                  key={s.placeId}
                  onPress={() => handleSelectSuggestion(s)}
                  accessibilityRole="button"
                  accessibilityLabel={s.primaryText}
                  style={[styles.candidate, { borderBottomColor: colors.border }]}>
                  <MapPin size={16} color={colors.textSecondary} />
                  <View style={styles.candidateText}>
                    <Text style={[styles.candidateLabel, { color: colors.text }]} numberOfLines={1}>
                      {s.primaryText}
                    </Text>
                    {!!s.secondaryText && (
                      <Text
                        style={[styles.candidateSub, { color: colors.textSecondary }]}
                        numberOfLines={1}>
                        {s.secondaryText}
                      </Text>
                    )}
                  </View>
                  <ChevronRight size={14} color={colors.textTertiary} />
                </TouchableOpacity>
              ))}
            </View>
          )}

          {/* Fallback: full-address lookup on submit when no suggestion fits. */}
          {candidateRows.length > 1 && (
            <View style={[styles.candidates, { borderTopColor: colors.border }]}>
              <Text style={[styles.candidatesHeader, { color: colors.textSecondary }]}>
                Which one?
              </Text>
              {candidateRows.map((r, idx) => (
                <TouchableOpacity
                  key={`${r.formattedAddress}-${idx}`}
                  onPress={() => applyResult(r)}
                  accessibilityRole="button"
                  accessibilityLabel={r.formattedAddress}
                  style={[styles.candidate, { borderBottomColor: colors.border }]}>
                  <MapPin size={16} color={colors.textSecondary} />
                  <Text
                    style={[styles.candidateLabel, styles.candidateText, { color: colors.text }]}
                    numberOfLines={2}>
                    {r.formattedAddress}
                  </Text>
                  <ChevronRight size={14} color={colors.textTertiary} />
                </TouchableOpacity>
              ))}
            </View>
          )}

          <ErrorBanner message={error} />

          {isGeocoding && (
            <Text style={[styles.helper, { color: colors.textTertiary }]}>Looking up address…</Text>
          )}

          {/* Secondary option: drop a pin directly on the map. */}
          <TouchableOpacity
            onPress={handleSkipToMap}
            accessibilityRole="button"
            accessibilityLabel="Drop a pin on the map instead"
            style={styles.mapLink}>
            {/* Ink, not pine: green is a category signal in Design B, and
                a green link in an otherwise monochrome sheet reads as one.
                Matches the sign-in screen's links (colors.text, 500). */}
            <Text style={[styles.mapLinkText, { color: colors.text }]}>
              Can&apos;t find it? Drop a pin on the map instead
            </Text>
          </TouchableOpacity>
        </>
      )}

      {step === 'confirm' && centroid && (
        <>
          <View style={styles.summaryBlock}>
            <Text style={[styles.summaryLine, { color: colors.text }]} numberOfLines={2}>
              {address || `${centroid.latitude.toFixed(5)}, ${centroid.longitude.toFixed(5)}`}
            </Text>
          </View>

          <LocationPickerMap centroid={centroid} onChange={handleMapChange} />

          <View style={styles.confirmActions}>
            <Button
              title="Edit address"
              onPress={() => setStep('search')}
              variant="secondary"
              fullWidth
              size="lg"
            />
            <Button title="Use this location" onPress={handleConfirm} fullWidth size="lg" />
          </View>
        </>
      )}
      {/* Scroll sentinel per sheet conventions. */}
      <View testID="location-picker-sheet-end" style={styles.scrollSentinel} />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  content: {
    padding: SPACING.lg,
    paddingBottom: SPACING.xxl,
    gap: SPACING.smd,
  },
  blurb: {
    ...TYPOGRAPHY.bodySmall,
    lineHeight: 20,
    marginBottom: SPACING.sm,
  },
  // The group opens with a hairline so the first row reads like the rest
  // (mockup .rowlink:first-of-type).
  candidates: {
    borderTopWidth: 1,
  },
  candidatesHeader: {
    ...TYPOGRAPHY.label,
    marginBottom: SPACING.xs,
  },
  // Hairline row (mockup .rowlink), not a bordered box.
  candidate: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.smd - 1,
    paddingVertical: SPACING.smd + 1,
    borderBottomWidth: 1,
  },
  candidateText: {
    flex: 1,
  },
  candidateLabel: {
    ...TYPOGRAPHY.label,
  },
  candidateSub: {
    ...TYPOGRAPHY.caption,
    marginTop: 2,
  },
  helper: {
    ...TYPOGRAPHY.bodySmall,
    textAlign: 'center',
  },
  scrollSentinel: {
    height: 1,
  },
  mapLink: {
    alignSelf: 'center',
    paddingVertical: SPACING.sm,
  },
  mapLinkText: {
    ...withWeight(TYPOGRAPHY.bodySmall, 'medium'),
  },
  summaryBlock: {
    gap: 2,
  },
  summaryLine: {
    ...TYPOGRAPHY.body,
    fontSize: FONT_SIZES.md,
  },
  confirmActions: {
    gap: SPACING.smd,
  },
});
